/**
 * Funds Page - Enhanced UI with modern gradient cards
 */

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchFunds } from '../redux/slices/portfolioSlice';

const Funds = () => {
  const dispatch = useDispatch();
  const { funds } = useSelector((state) => state.portfolio);

  useEffect(() => {
    dispatch(fetchFunds());
  }, [dispatch]);

  const fundCategories = {
    balance: [
      { 
        label: 'Opening Cash Balance', 
        value: funds?.openingCashLimit, 
        icon: '💰',
        gradient: 'from-green-400 via-emerald-500 to-green-600',
        shadow: 'shadow-green-500/25'
      },
      { 
        label: 'Trading Limit', 
        value: funds?.tradingLimit, 
        icon: '🎯',
        gradient: 'from-blue-400 via-indigo-500 to-blue-600',
        shadow: 'shadow-blue-500/25'
      },
    ],
    margins: [
      { 
        label: 'Collateral Margin', 
        value: funds?.collateralMargin, 
        icon: '🔒',
        gradient: 'from-purple-400 via-violet-500 to-purple-600',
        shadow: 'shadow-purple-500/25'
      },
      { 
        label: 'Utilized Margin', 
        value: funds?.utilizedMargin, 
        icon: '⚠️',
        gradient: 'from-orange-400 via-red-500 to-red-600',
        shadow: 'shadow-red-500/25'
      },
      { 
        label: 'Utilized SPAN Margin', 
        value: funds?.utilizedSpanMargin, 
        icon: '📊',
        gradient: 'from-cyan-400 via-teal-500 to-cyan-600',
        shadow: 'shadow-cyan-500/25'
      },
      { 
        label: 'Utilized Exposure Margin', 
        value: funds?.utilizedExposureMargin, 
        icon: '📈',
        gradient: 'from-pink-400 via-rose-500 to-pink-600',
        shadow: 'shadow-pink-500/25'
      },
    ],
    transactions: [
      { 
        label: 'Intraday Payin', 
        value: funds?.intradayPayin, 
        icon: '💳',
        gradient: 'from-emerald-400 via-green-500 to-emerald-600',
        shadow: 'shadow-emerald-500/25'
      },
      { 
        label: 'Credit for Sell', 
        value: funds?.creditForSell, 
        icon: '💹',
        gradient: 'from-lime-400 via-green-500 to-lime-600',
        shadow: 'shadow-lime-500/25'
      },
      { 
        label: 'Blocked for Payout', 
        value: funds?.blockedForPayout, 
        icon: '🚫',
        gradient: 'from-red-400 via-orange-500 to-red-600',
        shadow: 'shadow-red-500/25'
      },
      { 
        label: 'Adhoc Margin', 
        value: funds?.adhocMargin, 
        icon: '🎲',
        gradient: 'from-indigo-400 via-purple-500 to-indigo-600',
        shadow: 'shadow-indigo-500/25'
      },
    ]
  };

  const availableBalance = (funds?.openingCashLimit || 0) - (funds?.utilizedMargin || 0);

  const renderFundCard = (item) => (
    <div 
      key={item.label}
      className={`relative group overflow-hidden rounded-md bg-gradient-to-br ${item.gradient} p-3 text-white transform hover:scale-105 transition-all duration-300 ${item.shadow} hover:shadow-lg`}
    >
      <div className="absolute inset-0 bg-black/10 group-hover:bg-black/5 transition-all duration-300"></div>
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-1">
          <span className="text-lg">{item.icon}</span>
          <div className="bg-white/20 backdrop-blur-sm rounded-sm p-0.5">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4 2a2 2 0 00-2 2v11a2 2 0 002 2h12a2 2 0 002-2V4a2 2 0 00-2-2H4zm3 2h6v2H7V4z" clipRule="evenodd"/>
            </svg>
          </div>
        </div>
        <h3 className="text-xs font-medium mb-1 text-white/90 leading-tight">{item.label}</h3>
        <p className="text-base sm:text-lg font-bold text-white">
          ₹{(item.value || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
        </p>
        <div className="absolute -bottom-1 -right-1 w-8 h-8 bg-white/10 rounded-full transform rotate-45"></div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-100">
      <div className="max-w-full mx-auto space-y-4 p-2">
        {/* Header */}
        <div className="text-center py-2">
          <div className="flex items-center justify-center mb-3">
            <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mr-3 shadow-lg">
              <span className="text-xl">💰</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-green-600 via-emerald-600 to-green-700 bg-clip-text text-transparent">
                Funds & Limits
              </h1>
              <p className="text-sm text-gray-600">Manage your trading capital and limits</p>
            </div>
          </div>
        </div>

        {/* Available Balance - Hero Card */}
        <div className="relative overflow-hidden rounded-lg bg-gradient-to-br from-emerald-500 via-green-500 to-teal-600 p-3 text-white shadow-lg shadow-emerald-500/20 transform hover:scale-[1.01] transition-all duration-300 mx-2">
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="absolute -top-4 -right-4 w-8 h-8 bg-white/10 rounded-full"></div>
          <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-white/10 rounded-full"></div>
          <div className="relative z-10 text-center">
            <div className="flex items-center justify-center mb-1">
              <div className="bg-white/20 backdrop-blur-sm rounded-lg p-1.5">
                <span className="text-xl">💎</span>
              </div>
            </div>
            <h3 className="text-sm font-medium text-white/90 mb-1">Available for Trading</h3>
            <p className="text-xl sm:text-2xl font-bold text-white mb-1">
              ₹{availableBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
            </p>
            <div className="bg-white/20 backdrop-blur-sm rounded-md px-2 py-1 inline-block">
              <p className="text-xs text-white/90">Ready to invest • Real-time balance</p>
            </div>
          </div>
        </div>

        {/* Balance & Limits Section */}
        <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-white/20 p-3 mx-2">
          <div className="flex items-center mb-3">
            <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-md flex items-center justify-center mr-2">
              <span className="text-sm">💰</span>
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-800">Balance & Limits</h2>
              <p className="text-xs text-gray-600">Your available funds and trading limits</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {fundCategories.balance.map(renderFundCard)}
          </div>
        </div>

        {/* Margin Details Section */}
        <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-white/20 p-3 mx-2">
          <div className="flex items-center mb-3">
            <div className="w-6 h-6 bg-gradient-to-r from-purple-500 to-pink-500 rounded-md flex items-center justify-center mr-2">
              <span className="text-sm">📊</span>
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-800">Margin Utilization</h2>
              <p className="text-xs text-gray-600">Track your margin usage and requirements</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {fundCategories.margins.map(renderFundCard)}
          </div>
        </div>

        {/* Transactions Section */}
        <div className="bg-white/70 backdrop-blur-sm rounded-lg shadow-lg border border-white/20 p-3 mx-2">
          <div className="flex items-center mb-3">
            <div className="w-6 h-6 bg-gradient-to-r from-cyan-500 to-teal-500 rounded-md flex items-center justify-center mr-2">
              <span className="text-sm">💳</span>
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-800">Transaction Details</h2>
              <p className="text-xs text-gray-600">Payments, credits, and blocked amounts</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {fundCategories.transactions.map(renderFundCard)}
          </div>
        </div>

        {/* Information Section */}
        <div className="bg-gradient-to-r from-blue-50 via-indigo-50 to-purple-50 backdrop-blur-sm rounded-lg shadow-lg border border-blue-200/50 p-3 mx-2">
          <div className="flex items-center mb-3">
            <div className="w-6 h-6 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-md flex items-center justify-center mr-2">
              <span className="text-sm">💡</span>
            </div>
            <div>
              <h3 className="text-base font-bold text-gray-800">Fund Information</h3>
              <p className="text-xs text-gray-600">Understanding your fund details</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            <div className="bg-white/80 rounded-lg p-3 shadow-sm">
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                <span className="mr-2">📈</span> Trading Basics
              </h4>
              <div className="space-y-1 text-xs text-gray-700">
                <p><strong>Opening Cash Balance:</strong> Cash available at the start of trading</p>
                <p><strong>Trading Limit:</strong> Maximum amount available for trades</p>
              </div>
            </div>
            <div className="bg-white/80 rounded-lg p-3 shadow-sm">
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                <span className="mr-2">⚠️</span> Important Notes
              </h4>
              <div className="space-y-1 text-xs text-gray-700">
                <p><strong>Utilized Margin:</strong> Total margin used for open positions</p>
                <p><strong>Blocked for Payout:</strong> Funds held back, not available for trading</p>
              </div>
            </div>
            <div className="bg-white/80 rounded-lg p-3 shadow-sm">
              <h4 className="font-semibold text-gray-800 mb-2 flex items-center">
                <span className="mr-2">💳</span> Quick Tips
              </h4>
              <div className="space-y-1 text-xs text-gray-700">
                <p><strong>Real-time Balance:</strong> Updated automatically during trading</p>
                <p><strong>Margin Requirements:</strong> Varies by instrument and position size</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Funds;
